// Cálculo de liquidaciones laborales conforme al Código de Trabajo de Costa Rica.
// Referencias normativas:
//   - Art. 28: Preaviso (a cargo de quien rompe el contrato sin causa).
//   - Art. 29: Auxilio de cesantía (tabla con tope de 8 años).
//   - Art. 30: Forma de pago.
//   - Art. 81: Causales de despido sin responsabilidad patronal (causa justa).
//   - Art. 153: Vacaciones (1 día por mes laborado).
//   - Art. 157: Salario para vacaciones (promedio últimos 6 meses ordinarios).
//   - Ley 2412: Aguinaldo (Dic 1 – Nov 30 ÷ 12, proporcional).

import { subPeriodsForMonth, buildOverrideFromDoc } from "./subperiods";

// ----- Catálogo de motivos -----
// pagaPreaviso, pagaCesantia, pagaVacaciones, pagaAguinaldo
export const MOTIVOS_LIQUIDACION = [
  { id: "despido_con_responsabilidad", label: "Despido con responsabilidad patronal", articulo: "Art. 29-30", pagaPreaviso: true,  pagaCesantia: true,  pagaVacaciones: true, pagaAguinaldo: true },
  { id: "despido_sin_responsabilidad", label: "Despido sin responsabilidad patronal (causa justa)", articulo: "Art. 81", pagaPreaviso: false, pagaCesantia: false, pagaVacaciones: true, pagaAguinaldo: true },
  { id: "renuncia",                    label: "Renuncia del trabajador", articulo: "Art. 28", pagaPreaviso: false, pagaCesantia: false, pagaVacaciones: true, pagaAguinaldo: true },
  { id: "mutuo_acuerdo",               label: "Mutuo acuerdo", articulo: "—", pagaPreaviso: false, pagaCesantia: false, pagaVacaciones: true, pagaAguinaldo: true },
  { id: "muerte_trabajador",           label: "Muerte del trabajador", articulo: "Art. 85", pagaPreaviso: false, pagaCesantia: true,  pagaVacaciones: true, pagaAguinaldo: true },
  { id: "pension",                     label: "Pensión por vejez/invalidez", articulo: "—", pagaPreaviso: false, pagaCesantia: false, pagaVacaciones: true, pagaAguinaldo: true },
  { id: "vencimiento_plazo",           label: "Vencimiento del plazo del contrato", articulo: "—", pagaPreaviso: false, pagaCesantia: false, pagaVacaciones: true, pagaAguinaldo: true },
];
export const getMotivo = (id) => MOTIVOS_LIQUIDACION.find(m => m.id === id) || MOTIVOS_LIQUIDACION[0];

// ----- Preaviso (Art. 28) -----
// Aplica al que rompe el contrato (patrono o trabajador). Días en función de antigüedad.
export function diasPreaviso(meses) {
  if (meses < 3) return 0;
  if (meses < 6) return 7;   // > 3 ≤ 6 meses → 1 semana
  if (meses < 12) return 15; // > 6 < 12 meses → 15 días
  return 30;                 // ≥ 1 año → 1 mes
}

// ----- Cesantía (Art. 29 CT — fórmula acumulada oficial) -----
// Total de días de cesantía según los años reconocidos, con tope legal de 176.
//
// Redondeo: los meses fraccionarios del año en curso se suman al año siguiente
// SOLO si son mayores a 6 (más de medio año). Es decir:
//   1 año 6 meses → 1 año reconocido
//   1 año 7 meses → 2 años reconocidos
//   1 año 9 meses → 2 años reconocidos
//
// Tabla de días totales acumulados por año reconocido:
//   1 año  → 19.5
//   2 años → 40
//   3 años → 61.5
//   4 años → 84
//   5 años → 106.2
//   6 años → 129
//   ≥ 7 años → años × 22 (topado a 176 = 8 × 22)
//
// Menos de 1 año: 0 (< 3m), 7 (3–6m), 14 (6–12m).
const TOPE_DIAS_CESANTIA = 176;

export function diasCesantia(meses) {
  const aniosCompletos = Math.floor(meses / 12);
  const mesesResto = meses - aniosCompletos * 12;
  const anios = mesesResto > 6 ? aniosCompletos + 1 : aniosCompletos;

  let dias;
  if (anios >= 7) dias = anios * 22;
  else if (anios === 6) dias = 129;
  else if (anios === 5) dias = 106.2;
  else if (anios === 4) dias = 84;
  else if (anios === 3) dias = 61.5;
  else if (anios === 2) dias = 40;
  else if (anios === 1) dias = 19.5;
  else if (mesesResto >= 6) dias = 14;
  else if (mesesResto >= 3) dias = 7;
  else dias = 0;

  return Math.min(TOPE_DIAS_CESANTIA, Number(dias.toFixed(2)));
}

// ----- Tiempo laborado -----
export function tiempoLaborado(fechaIngreso, fechaSalida) {
  if (!fechaIngreso || !fechaSalida) return { meses: 0, anios: 0, mesesRestantes: 0, dias: 0 };
  const ing = new Date(`${fechaIngreso.slice(0,10)}T00:00:00`);
  const sal = new Date(`${fechaSalida.slice(0,10)}T00:00:00`);
  let meses = (sal.getFullYear() - ing.getFullYear()) * 12 + (sal.getMonth() - ing.getMonth());
  if (sal.getDate() < ing.getDate()) meses -= 1;
  meses = Math.max(0, meses);
  const dias = Math.max(0, Math.round((sal - ing) / 86400000));
  return { meses, anios: Math.floor(meses / 12), mesesRestantes: meses % 12, dias };
}

// ----- Agregar salarios de los últimos 12 meses naturales desde comprobantes -----
export function salariosUltimos12Meses(empleado, comprobantes, fechaSalida) {
  if (!empleado || !fechaSalida) return [];
  const propios = (comprobantes || []).filter(c => c.empleado_id === empleado.id && c.kind !== "aguinaldo");
  const sal = new Date(`${fechaSalida.slice(0,10)}T00:00:00`);
  const fin = new Date(sal.getFullYear(), sal.getMonth(), 1);          // mes de salida (1er día)
  const ini = new Date(sal.getFullYear(), sal.getMonth() - 11, 1);     // 11 meses atrás
  const meses = [];
  for (let d = new Date(ini); d <= fin; d.setMonth(d.getMonth() + 1)) {
    meses.push({ y: d.getFullYear(), m: d.getMonth() + 1, total: 0, especie: 0 });
  }
  propios.forEach(c => {
    const fi = (c.fecha_inicio || c.fecha_emision || "").slice(0,10);
    if (!fi) return;
    const dt = new Date(`${fi}T00:00:00`);
    const slot = meses.find(x => x.y === dt.getFullYear() && x.m === dt.getMonth() + 1);
    if (slot) slot.total += Number(c.monto_bruto) || 0;
  });
  return meses.map(m => ({
    label: `${["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Setiembre","Octubre","Noviembre","Diciembre"][m.m - 1]}`,
    year: m.y,
    salarios: Math.round(m.total),
    especie: m.especie,
  }));
}

// ----- Salarios de los últimos 12 meses LIVE (desde asistencia/rebajos/bonif
// + overrides manuales de planilla). Mismo enfoque que aguinaldoLive: calcula
// el bruto real de cada mes desglosando por sub-período (semanal Lun-Dom /
// quincenal / mensual) y aplicando los overrides manuales guardados en
// `planilla_overrides`. Los meses previos a fecha_ingreso se marcan
// `disabled: true` con salario 0.
export function salariosMensualesLive({
  empleado, empresa, allAsistencia, allRebajos, allBonif, allOverrides = [], fechaSalida,
  computePayrollForEmployee, puestosMap, puestoNoHorasExtras,
}) {
  if (!empleado || !fechaSalida) return [];
  const meses_es = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Setiembre","Octubre","Noviembre","Diciembre"];
  const sal = new Date(`${fechaSalida.slice(0,10)}T00:00:00`);
  const fin = new Date(sal.getFullYear(), sal.getMonth(), 1);
  const ini = new Date(sal.getFullYear(), sal.getMonth() - 11, 1);
  const fIng = empleado.fecha_ingreso ? new Date(`${empleado.fecha_ingreso.slice(0,10)}T00:00:00`) : null;
  const fIngMonth = fIng ? new Date(fIng.getFullYear(), fIng.getMonth(), 1) : null;
  const fechaIngreso = empleado.fecha_ingreso ? empleado.fecha_ingreso.slice(0, 10) : "";

  const grupoAct = empresa?.actividad_grupo;
  const ccssPensPct = Number(empresa?.ccss_pensionado_pct);
  const ccssPensRate = (isFinite(ccssPensPct) && ccssPensPct > 0) ? ccssPensPct / 100 : null;
  const noHorasExtras = puestoNoHorasExtras ? puestoNoHorasExtras(puestosMap, empleado?.puesto) : false;
  const freq = String(empleado.frecuencia_pago || "Mensual").toLowerCase();
  const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
  const ccssRate = ccssPensRate;

  const out = [];
  for (let d = new Date(ini); d <= fin; d.setMonth(d.getMonth() + 1)) {
    const y = d.getFullYear(); const m = d.getMonth();
    const monthStart = new Date(y, m, 1);
    const disabled = fIngMonth && monthStart < fIngMonth;

    let salario = 0;
    if (!disabled && computePayrollForEmployee) {
      const periods = subPeriodsForMonth(y, m, freq);
      for (const p of periods) {
        const ast = (allAsistencia || []).filter(a => {
          if (a.empleado_id !== empleado.id) return false;
          const f = (a.fecha || "").slice(0, 10);
          if (!f || f < p.start || f > p.end) return false;
          if (fechaIngreso && f < fechaIngreso) return false;
          return true;
        });
        const rebs = (allRebajos || []).filter(r => {
          if (r.empleado_id !== empleado.id || !r.activo) return false;
          const f = (r.fecha || r.fecha_inicio || "").slice(0, 10);
          if (!f || f < p.start || f > p.end) return false;
          if (fechaIngreso && f < fechaIngreso) return false;
          return true;
        });
        const bons = (allBonif || []).filter(b => {
          if (b.empleado_id !== empleado.id || !b.activo) return false;
          const f = (b.fecha || "").slice(0, 10);
          if (!f || f < p.start || f > p.end) return false;
          if (fechaIngreso && f < fechaIngreso) return false;
          return true;
        });
        const ovDoc = (allOverrides || []).find(x =>
          x.empleado_id === empleado.id &&
          x.fecha_inicio === p.start &&
          x.fecha_fin === p.end
        );
        const override = buildOverrideFromDoc(ovDoc);
        const calc = computePayrollForEmployee(empleado, ast, {
          rebajos: rebs, bonificaciones: bons,
          extrasDoblesFeriado: !!empresa.extras_dobles_feriado,
          ccssAplicaExtras: !!empresa.ccss_aplica_extras,
          noHorasExtras, diasBase, ccssRate, override,
          periodStart: p.start, periodEnd: p.end,
          jornada48: empresa?.actividad_grupo === "servicios_jornadas_especiales" && freq !== "semanal",
        });
        salario += Number(calc.totalBruto) || 0;
      }
    }

    out.push({ label: meses_es[m], year: y, month: m, salarios: salario, especie: 0, disabled: !!disabled });
  }
  return out;
}

// ----- Promedios salariales -----
export function promedioSalario(salariosMensuales, ultimos = 6) {
  const n = Math.min(ultimos, salariosMensuales.length);
  if (n === 0) return 0;
  const slice = salariosMensuales.slice(-n);
  const suma = slice.reduce((a, s) => a + (Number(s.salarios) + Number(s.especie || 0)), 0);
  return suma / n;
}

export function salarioDiarioPromedio(salariosMensuales, ultimos = 6) {
  return promedioSalario(salariosMensuales, ultimos) / 30;
}

// ----- Cálculo principal -----
// Bases legales:
//   Preaviso (Art. 30 CT): promedio ordinario de los ÚLTIMOS 6 MESES.
//   Cesantía (Art. 30 CT): promedio ordinario de los ÚLTIMOS 6 MESES.
//   Vacaciones (Art. 157 CT): promedio de las últimas 50 SEMANAS (~12 meses).
//   Aguinaldo (Ley 2412): suma de brutos desde el 1-dic del año anterior a
//     la salida hasta la fecha de salida, dividido entre 12. Meses previos
//     a diciembre NO se incluyen.
// adelanto = monto ya pagado (para calcular diferencia)
export function calcularLiquidacion({
  empleado, empresa = {}, fechaSalida, motivoId, salariosMensuales,
  diasVacacionesPendientes = 0, adelanto = 0,
  // Toggles manuales para anular un rubro (útil en mutuo acuerdo)
  toggles = {},
}) {
  const motivo = getMotivo(motivoId);
  const t = tiempoLaborado(empleado?.fecha_ingreso, fechaSalida);

  const prom6 = promedioSalario(salariosMensuales, 6);
  const promDiario6 = prom6 / 30;
  // Promedio 50 semanas (Art. 157 CT): suma de los últimos 12 meses / 11.5
  // (aproximación oficial usada en liquidaciones costarricenses; 11.5 refleja
  // que 50 semanas equivalen a ~11.5 meses efectivos).
  const suma12 = salariosMensuales.reduce(
    (a, s) => a + Number(s.salarios) + Number(s.especie || 0), 0
  );
  const prom50 = suma12 / 11.5;
  const promDiario50 = prom50 / 30;
  const totalSalarios = suma12;

  // ----- Aguinaldo proporcional (Ley 2412) -----
  // Suma de brutos desde el 1-diciembre del año anterior (o mismo año, si
  // la salida es en diciembre) hasta la fecha de salida. Meses previos
  // NO se contabilizan aunque estén en el array de últimos 12.
  const monthIdxFromLabel = (lbl) => {
    const M = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Setiembre","Octubre","Noviembre","Diciembre"];
    return M.indexOf(lbl);
  };
  let sumaAguinaldo = 0;
  if (fechaSalida) {
    const sd = new Date(`${fechaSalida.slice(0,10)}T00:00:00`);
    const lastDicYear = sd.getMonth() >= 11 ? sd.getFullYear() : sd.getFullYear() - 1;
    const cutoffKey = lastDicYear * 12 + 11;              // dic del año anterior
    const salidaKey = sd.getFullYear() * 12 + sd.getMonth();
    salariosMensuales.forEach(s => {
      const mIdx = (s.month !== undefined) ? s.month : monthIdxFromLabel(s.label);
      if (mIdx < 0) return;
      const k = s.year * 12 + mIdx;
      if (k >= cutoffKey && k <= salidaKey) {
        sumaAguinaldo += Number(s.salarios) + Number(s.especie || 0);
      }
    });
  }

  // Rubros (montos)
  const dPreaviso = motivo.pagaPreaviso ? diasPreaviso(t.meses) : 0;
  const dCesantia = motivo.pagaCesantia ? diasCesantia(t.meses) : 0;
  const includePreaviso = toggles.preaviso !== undefined ? toggles.preaviso : motivo.pagaPreaviso;
  const includeCesantia = toggles.cesantia !== undefined ? toggles.cesantia : motivo.pagaCesantia;
  const includeVacaciones = toggles.vacaciones !== undefined ? toggles.vacaciones : motivo.pagaVacaciones;
  const includeAguinaldo = toggles.aguinaldo !== undefined ? toggles.aguinaldo : motivo.pagaAguinaldo;

  // Preaviso y Cesantía: base = promedio 6 meses (Art. 30 CT)
  const montoPreaviso = includePreaviso ? dPreaviso * promDiario6 : 0;
  const montoCesantia = includeCesantia ? dCesantia * promDiario6 : 0;
  // Vacaciones: base = promedio 50 semanas (~12 meses) (Art. 157 CT)
  const montoVacaciones = includeVacaciones ? diasVacacionesPendientes * promDiario50 : 0;
  // Aguinaldo proporcional: suma desde diciembre anterior a salida / 12
  const montoAguinaldo = includeAguinaldo ? sumaAguinaldo / 12 : 0;

  // Salario diario contractual (base mensual)
  const salarioMensualBase = Number(empleado?.salario_base) || prom6;
  const salarioDiarioContractual = salarioMensualBase / 30;
  const jornadaHoras = {
    Diurna: 8, Mixta: 7, Nocturna: 6,
  };
  const horaOrdinaria = salarioDiarioContractual / (jornadaHoras[empleado?.jornada] || 8);
  const horaMixta = salarioDiarioContractual / 7;
  const horaNocturna = salarioDiarioContractual / 6;

  const totalLiquidacion = montoPreaviso + montoCesantia + montoVacaciones + montoAguinaldo;
  const diferencia = totalLiquidacion - Number(adelanto || 0);

  return {
    motivo, tiempo: t, totalSalarios,
    baseAguinaldo: sumaAguinaldo,
    prom6, promDiario6, prom50, promDiario50,
    dPreaviso, dCesantia,
    incluir: { preaviso: includePreaviso, cesantia: includeCesantia, vacaciones: includeVacaciones, aguinaldo: includeAguinaldo },
    montos: { preaviso: montoPreaviso, cesantia: montoCesantia, vacaciones: montoVacaciones, aguinaldo: montoAguinaldo },
    diasVacacionesPendientes,
    salarioDiarioContractual,
    horaOrdinaria, horaMixta, horaNocturna,
    totalLiquidacion,
    adelanto: Number(adelanto || 0),
    diferencia,
  };
}
